import { gql } from '@apollo/client';
import { select } from '@shared/constants/projectTarget';
import { mapKeys } from 'lodash-es';
import { GetParsedViewParams_CurrentStudyStateFragment } from '@ui/utils/getParsedViewParams.generated';

export type B2CUrlPathGroup =
  | 'todayMission'
  | 'learning'
  | 'summary'
  | 'grammar'
  | 'myGroup'
  | 'myNote'
  | 'accountSetting'
  | 'myLearningStatus'
  | 'myLearningStatusDetail'
  | 'aiTutor';

const B2CUrlPaths: Record<string, B2CUrlPathGroup> = {
  '/today-mission': 'todayMission',
  '/study-module/[studyModuleInstanceId]/[unitId]/[...param]': 'learning',
  '/study-module/:studyModuleInstanceId/:unitId/:studyActivityId': 'learning',
  '/math-ai-tutor': 'aiTutor',
  '/english-ai-tutor': 'aiTutor',
  '/korean-ai-tutor': 'aiTutor',
  '/study-module/:studyModuleInstanceId/:unitId/summary': 'summary',
  '/handwriting-note/[studyActivityInstanceId]': 'learning',
  '/grammar': 'grammar',
  '/my-group/[groupId]': 'myGroup',
  '/my-note': 'myNote',
  '/account-setting': 'accountSetting',
  '/my-learning-status/[groupId]': 'myLearningStatus',
  '/my-learning-status/[groupId]/detail/[curriculumId]': 'myLearningStatusDetail',
};
export const B2CUrlPathMap = mapKeys(B2CUrlPaths, (value, key) => {
  return (
    select({
      _default: '',
      schoolpt: '/[locale]/[domain]/s',
    }) + key
  );
});

type getParsedViewParamsResult =
  | {
    pathGroup: 'summary';
    studyActivityInstanceId: null;
    studyUnitInstanceId: string;
    studyModuleInstanceId: string;
  }
  | {
    pathGroup: 'learning';
    studyActivityInstanceId: string;
    studyUnitInstanceId: string;
    studyModuleInstanceId: string;
  }
  | {
    pathGroup: Exclude<B2CUrlPathGroup, 'summary' | 'learning'>;
    studyActivityInstanceId: null;
    studyUnitInstanceId: null;
    studyModuleInstanceId: null;
  }
  | null;

function getParsedViewParams(
  currentStudyState?: GetParsedViewParams_CurrentStudyStateFragment,
): getParsedViewParamsResult {
  if (!currentStudyState?.view) {
    return null;
  }

  const pathGroup = B2CUrlPathMap[currentStudyState.view.urlPathGroup || ''];

  if (!pathGroup) {
    return null;
  }

  if (
    currentStudyState.view.urlPathGroup === '/study-module/:studyModuleInstanceId/:unitId/:studyActivityId'
  ) {
    return {
      pathGroup: 'learning',
      studyActivityInstanceId: currentStudyState.view.params?.studyActivityId,
      studyUnitInstanceId: currentStudyState.view.params?.unitId,
      studyModuleInstanceId: currentStudyState.view.params?.studyModuleInstanceId,
    };
  }

  if (pathGroup === 'summary') {
    return {
      pathGroup: 'summary',
      studyActivityInstanceId: null,
      studyUnitInstanceId: currentStudyState.view.params?.unitId,
      studyModuleInstanceId: currentStudyState.view.params?.studyModuleInstanceId,
    };
  }

  // deprecated
  if (pathGroup === 'learning') {
    const firstParam = currentStudyState.view.params?.param?.[0];
    const studyUnitInstanceId = currentStudyState.view.params?.unitId;
    const studyModuleInstanceId = currentStudyState.view.params?.studyModuleInstanceId;
    if (firstParam === 'summary' && studyUnitInstanceId && studyModuleInstanceId) {
      return {
        pathGroup: 'summary',
        studyActivityInstanceId: null,
        studyUnitInstanceId,
        studyModuleInstanceId,
      };
    }

    if (firstParam && firstParam !== 'summary' && studyUnitInstanceId && studyModuleInstanceId) {
      return {
        pathGroup: 'learning',
        studyActivityInstanceId: firstParam,
        studyUnitInstanceId,
        studyModuleInstanceId,
      };
    }

    return null;
  }

  return {
    pathGroup,
    studyActivityInstanceId: null,
    studyUnitInstanceId: null,
    studyModuleInstanceId: null,
  };
}
export default getParsedViewParams;

gql`
  fragment getParsedViewParams_CurrentStudyState on CurrentStudyState {
    groupId
    managerId
    studentId
    view {
      urlPathGroup
      params
    }
  }
`;
